import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Sparkles,
  Layout,
  BookOpen,
  Download,
  FileText,
  RefreshCw,
  Loader2,
  PlayCircle,
  FolderOpen,
  Link as LinkIcon,
  ClipboardCopy,
  Play,
  CheckCircle,
  Clock,
  Video,
  Headphones,
  Image as ImageIcon,
  MessageCircle,
  Layers,
  RotateCcw,
  Info,
  User,
  Lock,
} from 'lucide-react';
import { useProjectSuggestions, useGenerateProjectSuggestions } from '@/hooks/useProjectSuggestions';
import { useAllContentProgress, useUpdateContentProgress, type ContentItem } from '@/hooks/useContent';
import { supabase } from '@/integrations/supabase/client';
import { paginateQuery } from '@/lib/paginateSupabase';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { ContentPlayerModal, type ContentProgressUpdate, type ContentType } from '@/components/ContentPlayerModal';
import { SubscriptionUpsellModal } from '@/components/SubscriptionUpsellModal';
import { useUserAccess } from '@/hooks/useUserAccess';
import { cn } from '@/lib/utils';

type ResourceKind = 'download' | 'link' | 'copy';

const resourceKind = (r: any): ResourceKind => {
  if (r?.resource_type === 'download' || r?.resource_type === 'link' || r?.resource_type === 'copy') return r.resource_type;
  if (r?.external_url) return 'link';
  if (r?.file_url) return 'download';
  return 'copy';
};

const RESOURCE_KIND_LABEL: Record<ResourceKind, string> = {
  download: 'Descargable',
  link: 'Link',
  copy: 'Copiar y pegar',
};

const ResourceActionLabel = ({ kind }: { kind: ResourceKind }) => {
  if (kind === 'download') return <><Download className="w-3 h-3" />Descargar</>;
  if (kind === 'link') return <><LinkIcon className="w-3 h-3" />Abrir enlace</>;
  return <><ClipboardCopy className="w-3 h-3" />Copiar</>;
};

const SOLUTION_TYPE_CATEGORY_MAP: Record<number, string> = {
  1: 'Creación de contenido con AI',
  2: 'Vibecoding y Creación de Aplicaciones con IA',
  3: 'Creación de asistentes / agente inteligente con AI',
  4: 'Automatizaciones con AI',
  5: 'Análisis de datos con IA',
};

interface ProjectContentLesson {
  id: string;
  title: string;
  duration: string;
  type: ContentType;
  completed: boolean;
  progress?: number;
  mandatory?: boolean;
  thumbnail: string;
  description: string;
  vimeo_player_url?: string | null;
  html_content?: string | null;
  url?: string | null;
  thumbnail_url?: string | null;
  author?: string | null;
  tags?: string[] | null;
  target_levels?: number[] | null;
  access_level?: string | null;
}

const toProjectContentLesson = (
  content: ContentItem & { category?: string | null },
  progress?: { completed: boolean; progress_percent: number }
): ProjectContentLesson => ({
  id: content.id,
  title: content.name,
  duration: content.duration ?? '10 min',
  type: mapContentType(content.content_type),
  completed: progress?.completed ?? false,
  progress: progress?.progress_percent ?? 0,
  mandatory: false,
  thumbnail: content.thumbnail_url ?? '',
  description: content.description ?? '',
  vimeo_player_url: content.vimeo_player_url ?? (content.url?.includes('player.vimeo.com') ? content.url : null),
  html_content: content.html_content,
  url: content.url,
  thumbnail_url: content.thumbnail_url,
  author: content.author,
  tags: content.tags,
  target_levels: content.target_levels,
  access_level: content.access_level,
});

const mapContentType = (dbType: string | null): ContentType => {
  switch (dbType) {
    case 'video':
    case 'Video': return 'video';
    case 'interactive':
    case 'Interactivo':
    case 'ova': return 'interactive';
    case 'reading':
    case 'Lectura':
    case 'article': return 'reading';
    case 'Recurso':
    case 'resource': return 'resource';
    case 'audio':
    case 'Audio': return 'audio';
    case 'infographic':
    case 'Infografía': return 'infographic';
    case 'lia-class':
    case 'Clase con LIA': return 'lia-class';
    default: return 'reading';
  }
};

function ProjectContentCard({
  lesson,
  index,
  onOpenLesson,
  onUncomplete,
  requiresSubscription = false,
  showAiBadge = false,
  reason,
}: {
  lesson: ProjectContentLesson;
  index: number;
  onOpenLesson: (lesson: ProjectContentLesson, index: number) => void;
  onUncomplete: (lessonId: string) => void;
  requiresSubscription?: boolean;
  showAiBadge?: boolean;
  reason?: string | null;
}) {
  const [isHovered, setIsHovered] = useState(false);

  const getLessonIcon = (type: ContentType) => {
    switch (type) {
      case 'video': return <Video className="w-3 h-3" />;
      case 'reading': return <FileText className="w-3 h-3" />;
      case 'resource': return <FileText className="w-3 h-3" />;
      case 'audio': return <Headphones className="w-3 h-3" />;
      case 'infographic': return <ImageIcon className="w-3 h-3" />;
      case 'lia-class': return <MessageCircle className="w-3 h-3" />;
      case 'interactive': return <Layers className="w-3 h-3" />;
    }
  };

  const getTypeLabel = (type: ContentType) => {
    switch (type) {
      case 'video': return 'Video';
      case 'reading': return 'Lectura';
      case 'resource': return 'Recurso';
      case 'audio': return 'Audio';
      case 'infographic': return 'Infografía';
      case 'lia-class': return 'Clase con LIA';
      case 'interactive': return 'Interactivo';
    }
  };

  return (
    <div
      className="group relative cursor-pointer transition-all duration-500"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => onOpenLesson(lesson, index)}
    >
      <div
        className={cn(
          'relative rounded-xl overflow-hidden transition-all duration-500 bg-card border border-border h-full',
          isHovered && 'scale-[1.02] z-20 shadow-2xl shadow-foreground/10 border-primary/50'
        )}
      >
        <div className="relative aspect-video overflow-hidden bg-muted">
          {lesson.thumbnail_url ? (
            <img
              src={lesson.thumbnail_url}
              alt={lesson.title}
              loading="lazy"
              className={cn(
                'absolute inset-0 w-full h-full object-cover transition-all duration-700',
                isHovered && 'scale-110'
              )}
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-primary/10 to-accent/10">
              {getLessonIcon(lesson.type)}
            </div>
          )}
          <div
            className={cn(
              'absolute inset-0 bg-gradient-to-t from-foreground/90 via-foreground/20 to-transparent transition-opacity duration-300',
              isHovered ? 'opacity-100' : 'opacity-60'
            )}
          />

          <div
            className={cn(
              'absolute inset-0 flex items-center justify-center transition-all duration-300',
              isHovered ? 'opacity-100' : 'opacity-0'
            )}
          >
            <div
              className={cn(
                'w-14 h-14 rounded-full bg-background/90 flex items-center justify-center transition-transform duration-300',
                isHovered && 'scale-110'
              )}
            >
              {lesson.type === 'video' && <Play className="w-6 h-6 text-foreground fill-foreground ml-1" />}
              {lesson.type === 'reading' && <BookOpen className="w-6 h-6 text-foreground" />}
              {lesson.type === 'audio' && <Headphones className="w-6 h-6 text-foreground" />}
              {lesson.type === 'infographic' && <ImageIcon className="w-6 h-6 text-foreground" />}
              {lesson.type === 'lia-class' && <MessageCircle className="w-6 h-6 text-foreground" />}
              {lesson.type === 'interactive' && <Layers className="w-6 h-6 text-foreground" />}
              {lesson.type === 'resource' && <FileText className="w-6 h-6 text-foreground" />}
            </div>
          </div>

          <div className="absolute top-3 right-3 px-2 py-1 rounded bg-foreground/70 backdrop-blur-sm text-background text-xs font-medium flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {lesson.duration}
          </div>

          <div className="absolute top-3 left-3 flex items-center gap-1.5">
            <div className="px-2 py-1 rounded bg-foreground/70 backdrop-blur-sm text-background text-xs font-medium flex items-center gap-1">
              {getLessonIcon(lesson.type)}
              {getTypeLabel(lesson.type)}
            </div>
            {showAiBadge && (
              <div className="px-2 py-1 rounded bg-secondary/90 backdrop-blur-sm text-secondary-foreground text-xs font-medium flex items-center gap-1">
                <Sparkles className="w-3 h-3" />
                IA
              </div>
            )}
            {requiresSubscription && (
              <div className="px-2 py-1 rounded bg-primary/90 backdrop-blur-sm text-primary-foreground text-xs font-semibold flex items-center gap-1">
                <Sparkles className="w-3 h-3" />
                Pro
              </div>
            )}
          </div>

          {lesson.completed && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onUncomplete(lesson.id);
              }}
              title="Desmarcar completado"
              className="absolute bottom-3 right-3 flex items-center gap-1 px-2 py-1 rounded-full bg-success/90 backdrop-blur-sm hover:bg-destructive/90 transition-colors group/uncomplete z-20"
            >
              <CheckCircle className="w-4 h-4 text-success-foreground group-hover/uncomplete:hidden" />
              <RotateCcw className="w-4 h-4 text-destructive-foreground hidden group-hover/uncomplete:block" />
              <span className="text-background text-xs font-medium group-hover/uncomplete:hidden">Completado</span>
              <span className="text-background text-xs font-medium hidden group-hover/uncomplete:block">Desmarcar</span>
            </button>
          )}

          {!lesson.completed && lesson.progress && lesson.progress > 0 && (
            <div className="absolute bottom-3 right-3 flex items-center gap-1 px-2 py-1 rounded-full bg-warning/90 backdrop-blur-sm">
              <PlayCircle className="w-4 h-4 text-background" />
              <span className="text-background text-xs font-medium">{lesson.progress}%</span>
            </div>
          )}

          {(lesson.description || lesson.author || (lesson.tags && lesson.tags.length > 0) || (lesson.target_levels && lesson.target_levels.length > 0)) && (
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  onClick={(e) => e.stopPropagation()}
                  className="absolute bottom-3 left-3 w-7 h-7 rounded-full bg-foreground/70 backdrop-blur-sm flex items-center justify-center text-background hover:bg-primary transition-colors z-20"
                >
                  <Info className="w-3.5 h-3.5" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-80 text-sm" side="top" align="start" onClick={(e) => e.stopPropagation()}>
                <div className="space-y-3">
                  <h4 className="font-semibold text-foreground line-clamp-2">{lesson.title}</h4>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">{getLessonIcon(lesson.type)}{getTypeLabel(lesson.type)}</span>
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{lesson.duration}</span>
                  </div>
                  {lesson.description && <p className="text-muted-foreground text-xs leading-relaxed">{lesson.description}</p>}
                  {reason && <p className="text-muted-foreground text-xs italic"><span className="font-medium not-italic text-primary">Por qué: </span>{reason}</p>}
                  {lesson.author && (
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <User className="w-3.5 h-3.5" />
                      <span>{lesson.author}</span>
                    </div>
                  )}
                  {lesson.target_levels && lesson.target_levels.length > 0 && (
                    <div className="space-y-1">
                      <span className="text-[10px] font-medium text-muted-foreground uppercase">Dirigido a</span>
                      <div className="flex flex-wrap gap-1">
                        {[...lesson.target_levels].sort((a, b) => a - b).map((lvl) => (
                          <Badge key={lvl} variant="outline" className="text-[10px] px-1.5 py-0">Nivel {lvl}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  {lesson.tags && lesson.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {lesson.tags.map((tag) => <Badge key={tag} variant="secondary" className="text-[10px] px-2 py-0">{tag}</Badge>)}
                    </div>
                  )}
                </div>
              </PopoverContent>
            </Popover>
          )}

          {requiresSubscription && (
            <div
              className={cn(
                'absolute inset-0 flex flex-col items-center justify-center bg-foreground/70 backdrop-blur-[2px] gap-2 transition-opacity duration-300',
                isHovered ? 'opacity-100' : 'opacity-80'
              )}
            >
              <Lock className="w-8 h-8 text-primary" />
              <span className="text-background text-xs font-semibold px-3 text-center">Suscríbete para acceder</span>
            </div>
          )}
        </div>

        <div className="p-4">
          <h4 className="font-semibold text-foreground text-sm line-clamp-2 mb-1 group-hover:text-primary transition-colors">
            {lesson.title}
          </h4>
          <div
            className={cn(
              'overflow-hidden transition-all duration-500',
              isHovered ? 'max-h-28 opacity-100 mt-2' : 'max-h-0 opacity-0'
            )}
          >
            <p className="text-xs text-muted-foreground line-clamp-2 mb-3">{reason || lesson.description}</p>
            <div className="flex gap-2">
              <Button
                size="sm"
                className="flex-1 h-8 text-xs"
                onClick={(event) => {
                  event.stopPropagation();
                  onOpenLesson(lesson, index);
                }}
              >
                {lesson.completed ? (
                  <><RotateCcw className="w-3 h-3 mr-1" />Volver a ver</>
                ) : lesson.progress && lesson.progress > 0 ? (
                  <><Play className="w-3 h-3 mr-1" />Continuar</>
                ) : (
                  <><Play className="w-3 h-3 mr-1" />Iniciar</>
                )}
              </Button>
            </div>
          </div>
        </div>

        {lesson.completed ? (
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-success" />
        ) : lesson.progress && lesson.progress > 0 ? (
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-muted">
            <div className="h-full bg-warning transition-all duration-300" style={{ width: `${lesson.progress}%` }} />
          </div>
        ) : null}
      </div>
    </div>
  );
}

interface Props {
  projectId: string;
  solutionTypeInt: number | null;
}

export function ProjectSuggestionsPanel({ projectId, solutionTypeInt }: Props) {
  const queryClient = useQueryClient();
  const { hasPaidAccess, isLoading: accessLoading } = useUserAccess();
  const { data: suggestions, isLoading } = useProjectSuggestions(projectId);
  const generate = useGenerateProjectSuggestions();
  const { data: progressRecords } = useAllContentProgress();
  const updateProgress = useUpdateContentProgress();
  const [selectedLesson, setSelectedLesson] = useState<ProjectContentLesson | null>(null);
  const [selectedLessonIndex, setSelectedLessonIndex] = useState(0);
  const [isPlayerOpen, setIsPlayerOpen] = useState(false);
  const [upsellOpen, setUpsellOpen] = useState(false);
  const [progressOverrides, setProgressOverrides] = useState<Record<string, { completed: boolean; progress_percent: number }>>({});

  // Auto-generate on first open if no suggestions
  useEffect(() => {
    if (!isLoading && !suggestions && !generate.isPending) {
      generate.mutate({ projectId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, suggestions, projectId]);

  // Filtered template list (existing behavior)
  const { data: dbTemplates = [] } = useQuery({
    queryKey: ['templates-for-project', solutionTypeInt],
    queryFn: async () => {
      let q = supabase.from('templates').select('*').order('usage_count', { ascending: false });
      if (solutionTypeInt) q = q.contains('solution_types', [solutionTypeInt]);
      const { data, error } = await q;
      if (error) throw error;
      return data ?? [];
    },
  });

  const handleUseTemplate = async (template: any) => {
    const kind = resourceKind(template);
    if (kind === 'link' && template.external_url) {
      window.open(template.external_url, '_blank');
    } else if (kind === 'download' && template.file_url) {
      window.open(template.file_url, '_blank');
    } else if (template.content) {
      try {
        await navigator.clipboard.writeText(template.content);
        toast.success('Contenido copiado al portapapeles');
      } catch {
        toast.error('No se pudo copiar');
      }
    } else {
      toast.error('Este recurso no tiene contenido disponible');
    }
    await supabase
      .from('templates')
      .update({ usage_count: (template.usage_count || 0) + 1 })
      .eq('id', template.id);
  };

  const projectCategory = solutionTypeInt ? SOLUTION_TYPE_CATEGORY_MAP[solutionTypeInt] ?? null : null;

  const progressMap = useMemo(() => {
    const map = new Map<string, { completed: boolean; progress_percent: number }>();
    (progressRecords ?? []).forEach((p) => {
      map.set(p.content_id, {
        completed: p.completed ?? false,
        progress_percent: p.progress_percent ?? 0,
      });
    });
    Object.entries(progressOverrides).forEach(([id, val]) => {
      map.set(id, val);
    });
    return map;
  }, [progressRecords, progressOverrides]);

  // Contenidos publicados de la categoría del proyecto
  const { data: categoryContents = [], isLoading: loadingCategory } = useQuery({
    queryKey: ['category-contents-for-project', projectCategory],
    enabled: !!projectCategory,
    queryFn: async () => {
      if (!projectCategory) return [];
      return await paginateQuery<any>((from, to) =>
        supabase
          .from('content_bank')
          .select('*')
          .eq('planning_status', 'published')
          .eq('category', projectCategory)
          .order('sort_order', { ascending: true })
          .order('name', { ascending: true })
          .range(from, to)
      );
    },
  });

  const CONTENT_PAGE_SIZE = 6;
  const contentGridRef = useRef<HTMLDivElement>(null);
  const [contentPage, setContentPage] = useState(1);
  const totalCategoryContents = categoryContents.length;
  const totalContentPages = Math.max(1, Math.ceil(totalCategoryContents / CONTENT_PAGE_SIZE));
  const safeContentPage = Math.min(contentPage, totalContentPages);
  const pageCategoryContents = useMemo(
    () => categoryContents.slice((safeContentPage - 1) * CONTENT_PAGE_SIZE, safeContentPage * CONTENT_PAGE_SIZE),
    [categoryContents, safeContentPage]
  );
  const changeContentPage = (p: number) => {
    setContentPage(p);
    contentGridRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  useEffect(() => {
    setContentPage(1);
  }, [projectId, solutionTypeInt]);

  const suggestedContent = suggestions?.suggested_content ?? [];


  const suggestedContentIds = useMemo(
    () => suggestedContent.map((c) => c.content_id).filter(Boolean),
    [suggestedContent]
  );

  const { data: suggestedContentRows = [], isLoading: loadingSuggestedContentRows } = useQuery({
    queryKey: ['suggested-content-rows-for-project', projectId, suggestedContentIds],
    enabled: suggestedContentIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('content_bank')
        .select('*')
        .in('id', suggestedContentIds)
        .eq('planning_status', 'published');
      if (error) throw error;
      return data ?? [];
    },
  });

  const suggestedContentReasonMap = useMemo(() => {
    const map = new Map<string, string>();
    suggestedContent.forEach((c) => map.set(c.content_id, c.reason));
    return map;
  }, [suggestedContent]);

  const suggestedContentLessons = useMemo(() => {
    const rowMap = new Map(suggestedContentRows.map((row: any) => [row.id, row]));
    return suggestedContent
      .map((item) => rowMap.get(item.content_id) ?? {
        id: item.content_id,
        name: item.name,
        content_type: item.content_type,
        category: item.category,
        description: item.description,
        duration: item.duration,
        thumbnail_url: item.thumbnail_url,
        url: null,
        html_content: null,
        vimeo_player_url: null,
        author: null,
        tags: null,
        target_levels: null,
        access_level: 'free',
      })
      .map((row: any) => toProjectContentLesson(row, progressMap.get(row.id)));
  }, [suggestedContent, suggestedContentRows, progressMap]);

  const categoryLessons = useMemo(
    () => categoryContents.map((content: any) => toProjectContentLesson(content, progressMap.get(content.id))),
    [categoryContents, progressMap]
  );
  const suggestedTemplates = suggestions?.suggested_templates ?? [];
  const suggestedTemplateIds = new Set(suggestedTemplates.map((t) => t.template_id));
  const otherTemplates = dbTemplates.filter((t: any) => !suggestedTemplateIds.has(t.id));

  // --- Paginación de "Recursos para tu proyecto" (6 por página) ---
  const RESOURCES_PAGE_SIZE = 6;
  const gridRef = useRef<HTMLDivElement>(null);
  const [page, setPage] = useState(1);
  const allResources = useMemo(
    () => [...suggestedTemplates, ...otherTemplates],
    [suggestions, dbTemplates] // eslint-disable-line react-hooks/exhaustive-deps
  );
  const totalResources = allResources.length;
  const totalPages = Math.max(1, Math.ceil(totalResources / RESOURCES_PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageResources = useMemo(
    () => allResources.slice((safePage - 1) * RESOURCES_PAGE_SIZE, safePage * RESOURCES_PAGE_SIZE),
    [allResources, safePage]
  );

  const changePage = (p: number) => {
    setPage(p);
    gridRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Reset pagination when the project, solution type or suggestions change
  useEffect(() => {
    setPage(1);
  }, [projectId, solutionTypeInt, suggestions]);

  const isGenerating = generate.isPending;

  const handleOpenLesson = (lesson: ProjectContentLesson, index: number) => {
    if (lesson.access_level === 'subscription' && !accessLoading && !hasPaidAccess) {
      setUpsellOpen(true);
      return;
    }
    setSelectedLesson(lesson);
    setSelectedLessonIndex(index);
    setIsPlayerOpen(true);
  };

  const handleClosePlayer = () => {
    setIsPlayerOpen(false);
    setSelectedLesson(null);
    queryClient.invalidateQueries({ queryKey: ['all-content-progress'] });
    queryClient.invalidateQueries({ queryKey: ['content-progress'] });
    // El widget de progreso del inicio y el progreso semanal salen de aquí.
    queryClient.invalidateQueries({ queryKey: ['weekly-progress'] });
  };

  const handleProgressSaved = useCallback((progress: ContentProgressUpdate) => {
    setProgressOverrides((prev) => ({
      ...prev,
      [progress.contentId]: {
        completed: progress.completed,
        progress_percent: progress.progressPercent,
      },
    }));
    queryClient.invalidateQueries({ queryKey: ['all-content-progress'] });
    queryClient.invalidateQueries({ queryKey: ['content-progress'] });
    // El widget de progreso del inicio y el progreso semanal salen de aquí.
    queryClient.invalidateQueries({ queryKey: ['weekly-progress'] });
  }, [queryClient]);

  const handleLessonComplete = (lessonId: string) => {
    setProgressOverrides((prev) => ({ ...prev, [lessonId]: { completed: true, progress_percent: 100 } }));
    updateProgress.mutate({ content_id: lessonId, completed: true, progress_percent: 100 });
  };

  const handleLessonUncomplete = (lessonId: string) => {
    setProgressOverrides((prev) => ({ ...prev, [lessonId]: { completed: false, progress_percent: 0 } }));
    updateProgress.mutate({ content_id: lessonId, completed: false, progress_percent: 0 });
  };

  return (
    <div className="space-y-6">
      {/* Header with regenerate */}
      <Card className="glass-card">
        <CardHeader>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-primary" />
                Contenido y Plantillas
              </CardTitle>
              <CardDescription>
                Recomendaciones generadas con IA según tu proyecto
              </CardDescription>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="gap-2"
              onClick={() => generate.mutate({ projectId, force: true })}
              disabled={isGenerating}
            >
              {isGenerating ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              Regenerar IA
            </Button>
          </div>
        </CardHeader>
      </Card>

      {/* Suggested Content */}
      <Card className="glass-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <BookOpen className="w-5 h-5 text-primary" />
            Contenido sugerido para tu proyecto
          </CardTitle>
          <CardDescription>Lecciones, videos y recursos recomendados por IA</CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="others">
            <TabsList className="mb-4">
              <TabsTrigger value="others">Contenidos sugeridos</TabsTrigger>
              <TabsTrigger value="category" disabled={!projectCategory}>
                Todos los contenidos de la categoría
              </TabsTrigger>
            </TabsList>


            <TabsContent value="category">
              {!projectCategory ? (
                <div className="text-center py-6">
                  <FolderOpen className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                  <p className="text-sm text-muted-foreground">
                    Define el tipo de proyecto para ver los contenidos de su categoría.
                  </p>
                </div>
              ) : loadingCategory ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {[1, 2, 3].map((i) => <Skeleton key={i} className="h-40" />)}
                </div>
              ) : totalCategoryContents === 0 ? (
                <div className="text-center py-6">
                  <BookOpen className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                  <p className="text-sm text-muted-foreground">Todavía no hay contenidos en esta categoría.</p>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" ref={contentGridRef}>
                    {pageCategoryContents.map((c: any, idx: number) => (
                      <ProjectContentCard
                        key={c.id}
                        lesson={categoryLessons.find((lesson) => lesson.id === c.id) ?? toProjectContentLesson(c, progressMap.get(c.id))}
                        index={(safeContentPage - 1) * CONTENT_PAGE_SIZE + idx}
                        onOpenLesson={handleOpenLesson}
                        onUncomplete={handleLessonUncomplete}
                        requiresSubscription={c.access_level === 'subscription' && !accessLoading && !hasPaidAccess}
                      />
                    ))}
                  </div>
                  {totalContentPages > 1 && (
                    <div className="flex items-center justify-center gap-3 mt-6">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={safeContentPage <= 1}
                        onClick={() => changeContentPage(safeContentPage - 1)}
                      >
                        Anterior
                      </Button>
                      <span className="text-sm text-muted-foreground">
                        Página {safeContentPage} de {totalContentPages} ({totalCategoryContents} contenidos)
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={safeContentPage >= totalContentPages}
                        onClick={() => changeContentPage(safeContentPage + 1)}
                      >
                        Siguiente
                      </Button>
                    </div>
                  )}
                </>
              )}
            </TabsContent>

            <TabsContent value="others">
              {isLoading || loadingSuggestedContentRows || (isGenerating && suggestedContent.length === 0) ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {[1, 2, 3].map((i) => <Skeleton key={i} className="h-40" />)}
                </div>
              ) : suggestedContent.length === 0 ? (
                <div className="text-center py-6">
                  <BookOpen className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                  <p className="text-sm text-muted-foreground">Aún no hay contenido sugerido. Pulsa "Regenerar IA".</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {suggestedContentLessons.map((lesson, idx) => (
                    <ProjectContentCard
                      key={lesson.id}
                      lesson={lesson}
                      index={idx}
                      onOpenLesson={handleOpenLesson}
                      onUncomplete={handleLessonUncomplete}
                      requiresSubscription={lesson.access_level === 'subscription' && !accessLoading && !hasPaidAccess}
                      showAiBadge
                      reason={suggestedContentReasonMap.get(lesson.id)}
                    />
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>


      {/* Unified Resources */}
      <Card className="glass-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Sparkles className="w-5 h-5 text-secondary" />
            Recursos para tu proyecto
          </CardTitle>
          <CardDescription>Recomendados por IA y otros recursos según tu tipo de solución</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading || (isGenerating && suggestedTemplates.length === 0 && otherTemplates.length === 0) ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[1, 2, 3].map((i) => <Skeleton key={i} className="h-40" />)}
            </div>
          ) : suggestedTemplates.length === 0 && otherTemplates.length === 0 ? (
            <div className="text-center py-6">
              <Layout className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">No hay recursos disponibles todavía.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" ref={gridRef}>
              {pageResources.map((item: any) => {
                const isAi = 'template_id' in item;
                if (isAi) {
                  const t = item;
                  const full = dbTemplates.find((x: any) => x.id === t.template_id) || t;
                  const kind = resourceKind(full);
                  return (
                    <Card key={t.template_id} className="border border-secondary/30 hover:border-secondary/60 hover:bg-secondary/5 transition-all">
                      <CardContent className="p-4 space-y-3">
                        <div className="flex items-start justify-between gap-2">
                          <h4 className="font-semibold text-foreground line-clamp-2">{t.name}</h4>
                          <Badge variant="secondary" className="shrink-0 text-[10px] gap-1"><Sparkles className="w-3 h-3" />IA</Badge>
                        </div>
                        <div className="flex flex-wrap gap-1">
                          <Badge variant="outline" className="text-[10px]">{RESOURCE_KIND_LABEL[kind]}</Badge>
                          {t.category && <Badge variant="outline" className="text-[10px]">{t.category}</Badge>}
                        </div>
                        <p className="text-xs text-muted-foreground italic line-clamp-2">
                          <span className="font-medium not-italic text-secondary">Por qué aplica: </span>{t.reason}
                        </p>
                        <Button variant="outline" size="sm" className="w-full gap-1" onClick={() => handleUseTemplate(full)}>
                          <ResourceActionLabel kind={kind} />
                        </Button>
                      </CardContent>
                    </Card>
                  );
                }
                const template = item;
                const kind = resourceKind(template);
                return (
                  <Card key={template.id} className="border border-border hover:border-primary/50 hover:bg-primary/5 transition-all">
                    <CardContent className="p-4 space-y-3">
                      <h4 className="font-semibold text-foreground line-clamp-2">{template.name}</h4>
                      <div className="flex flex-wrap gap-1">
                        <Badge variant="outline" className="text-xs">{RESOURCE_KIND_LABEL[kind]}</Badge>
                        {template.category && <Badge variant="secondary" className="text-xs">{template.category}</Badge>}
                      </div>
                      <p className="text-xs text-muted-foreground line-clamp-2">{template.description}</p>
                      <Button variant="outline" size="sm" className="w-full gap-1" onClick={() => handleUseTemplate(template)}>
                        <ResourceActionLabel kind={kind} />
                      </Button>
                      <p className="text-[10px] text-muted-foreground">{template.usage_count || 0} usos</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-3 mt-6">
              <Button
                variant="outline"
                size="sm"
                className="gap-1"
                disabled={safePage <= 1}
                onClick={() => changePage(safePage - 1)}
              >
                Anterior
              </Button>
              <span className="text-sm text-muted-foreground">
                Página {safePage} de {totalPages} ({totalResources} recursos)
              </span>
              <Button
                variant="outline"
                size="sm"
                className="gap-1"
                disabled={safePage >= totalPages}
                onClick={() => changePage(safePage + 1)}
              >
                Siguiente
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <ContentPlayerModal
        isOpen={isPlayerOpen}
        onClose={handleClosePlayer}
        lesson={selectedLesson}
        lessonIndex={selectedLessonIndex}
        onComplete={handleLessonComplete}
        onProgressSaved={handleProgressSaved}
      />

      <SubscriptionUpsellModal open={upsellOpen} onOpenChange={setUpsellOpen} />
    </div>
  );
}
